import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchPaperDetails, downloadPaper } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import CitationModal from '../components/CitationModal';

const PaperDetails = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showCitationModal, setShowCitationModal] = useState(false);

  useEffect(() => {
    const loadPaperDetails = async () => {
      try {
        setLoading(true);
        const data = await fetchPaperDetails(id);
        setPaper(data);
      } catch (err) {
        console.error('Error loading paper details:', err);
        setError('Failed to load paper details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadPaperDetails();
  }, [id]);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await downloadPaper(id);
    } catch (err) {
      console.error('Error downloading paper:', err);
      alert('Failed to download paper. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  const formatAuthors = (authors) => {
    if (!authors || !Array.isArray(authors) || authors.length === 0) {
      return <span>Unknown Author</span>;
    }
    return authors.map((author, index) => (
      <span key={index}>
        {typeof author === 'object' ? author.name : author}
        {author.affiliation && <small className="text-muted"> ({author.affiliation})</small>}
        {author.isPrimaryContact && <sup>*</sup>}
        {index < authors.length - 1 && ', '}
      </span>
    ));
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PUBLISHED':
        return 'bg-success';
      case 'ACCEPTED':
        return 'bg-success';
      case 'REJECTED':
        return 'bg-danger';
      case 'UNDER_REVIEW':
        return 'bg-primary';
      case 'REVISIONS_REQUIRED':
        return 'bg-warning';
      case 'SUBMITTED':
        return 'bg-info';
      case 'WITHDRAWN':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  };

  const isAuthor = paper && user && paper.authors && Array.isArray(paper.authors) && paper.authors.some(author =>
    author.email === user.email
  );

  const isEditorOrAdmin = user && (user.role === 'EDITOR' || user.role === 'ADMIN');

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading paper details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/search" className="btn btn-primary">
          Back to Search
        </Link>
      </div>
    );
  }

  if (!paper) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          Paper not found or you don't have permission to view it.
        </div>
        <Link to="/search" className="btn btn-primary">
          Back to Search
        </Link>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          {/* Paper Status - Only visible to authors, editors, and admins */}
          {(isAuthor || isEditorOrAdmin) && paper.status !== 'PUBLISHED' && (
            <div className="alert alert-info mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Status:</strong>
                  <span className={`badge ms-2 ${getStatusBadgeClass(paper.status)}`}>
                    {paper.status.replace(/_/g, ' ')}
                  </span>
                </div>
                {isAuthor && paper.status === 'REVISIONS_REQUIRED' && (
                  <Link to={`/submit-paper?revision=${paper.id}`} className="btn btn-sm btn-warning">
                    Submit Revision
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Paper Details */}
          <div className="card mb-4">
            <div className="card-body">
              <h1 className="mb-3">{paper.title}</h1>

              <div className="mb-4">
                <h5 className="text-muted">
                  {formatAuthors(paper.authors)}
                </h5>
                {paper.authors && Array.isArray(paper.authors) && paper.authors.some(author => author.isPrimaryContact) && (
                  <p className="small mb-0">
                    <sup>*</sup> Corresponding author
                  </p>
                )}
              </div>

              <div className="paper-meta">
                {paper.volume && paper.issue && (
                  <div>
                    <strong>Published in</strong>
                    Volume {paper.volume}, Issue {paper.issue}
                    {paper.pages && <span>, Pages {paper.pages}</span>}
                  </div>
                )}

                {paper.doi && (
                  <div>
                    <strong>DOI</strong>
                    <a href={`https://doi.org/${paper.doi}`} target="_blank" rel="noreferrer">
                      {paper.doi}
                    </a>
                  </div>
                )}

                {(paper.publishedDate || paper.submittedDate || (paper.journalIssue && paper.journalIssue.publicationDate)) && (
                  <div>
                    <strong>Published On</strong>
                    {new Date(paper.publishedDate || paper.submittedDate || (paper.journalIssue && paper.journalIssue.publicationDate)).toLocaleDateString()}
                  </div>
                )}

                <div>
                  <strong>Category</strong>
                  {paper.category}
                </div>
              </div>

              <div className="paper-abstract mb-4">
                <h3>Abstract</h3>
                <p>{paper.abstract}</p>
              </div>

              <div className="mb-4">
                <h3>Keywords</h3>
                <p>
                  {paper.keywords.split(',').map((keyword, index) => (
                    <span key={index} className="badge bg-light text-dark me-2 mb-2 p-2">
                      {keyword.trim()}
                    </span>
                  ))}
                </p>
              </div>

              {/* Download and Cite Buttons - For published papers (all visitors) or authors/editors/admins for other statuses */}
              {(paper.status === 'PUBLISHED' || isAuthor || isEditorOrAdmin) && (
                <div className="mb-3 d-flex gap-2 flex-wrap">
                  <button
                    className="btn btn-primary"
                    onClick={handleDownload}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download me-2"></i> Download Full Paper
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setShowCitationModal(true)}
                  >
                    <i className="fas fa-quote-right me-2"></i> Cite This Paper
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Additional Information */}
          {(paper.status === 'PUBLISHED' || isAuthor || isEditorOrAdmin) && (
            <>
              {paper.fundingInfo && (
                <div className="card mb-3">
                  <div className="card-header">
                    <h4 className="mb-0">Funding Information</h4>
                  </div>
                  <div className="card-body">
                    <p className="mb-0">{paper.fundingInfo}</p>
                  </div>
                </div>
              )}

              {paper.acknowledgments && (
                <div className="card mb-3">
                  <div className="card-header">
                    <h4 className="mb-0">Acknowledgments</h4>
                  </div>
                  <div className="card-body">
                    <p className="mb-0">{paper.acknowledgments}</p>
                  </div>
                </div>
              )}

              {/* Reviewer Comments - Only for authors, editors, and admins */}
              {(isAuthor || isEditorOrAdmin) && paper.reviews && paper.reviews.length > 0 && (
                <div className="card mb-3">
                  <div className="card-header">
                    <h4 className="mb-0">Reviewer Comments</h4>
                  </div>
                  <div className="card-body">
                    {paper.reviews.map((review, index) => (
                      <div key={index} className="mb-3 pb-3 border-bottom">
                        <div className="d-flex justify-content-between mb-2">
                          <h5>Reviewer {index + 1}</h5>
                          <span className="badge bg-secondary">
                            {new Date(review.date).toLocaleDateString()}
                          </span>
                        </div>
                        <p>{review.comments}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Navigation */}
          <div className="d-flex justify-content-between mt-4">
            <Link to="/current-publications" className="btn btn-outline-secondary">
              <i className="fas fa-arrow-left me-2"></i> Back to Publications
            </Link>
            {user && isAuthor ? (
              <Link to="/dashboard" className="btn btn-outline-primary">
                <i className="fas fa-user me-2"></i> My Dashboard
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      {/* Citation Modal */}
      {showCitationModal && (
        <CitationModal
          paper={paper}
          onClose={() => setShowCitationModal(false)}
        />
      )}
    </div>
  );
};

export default PaperDetails;
